import type { Deal } from "../types";

/**
 * Almikia lead pipeline states — the single shared source of truth for the
 * 10 stage values and the partitions later tickets consume (kanban columns,
 * states shown outside the kanban, the inbox state, and the POSI-gated
 * states). Keep this the only place that lists these values: every consumer
 * (kanban layout, dashboard widgets, POSI gating) imports from here instead
 * of re-declaring the list.
 */

/** The inbox state: a lead lands here first, is not a kanban column (24h SLA). */
export const INBOX_STAGE = "prospecto" as const;

/** Kanban columns, in pipeline order (TASK-014). */
export const KANBAN_STAGES = [
  "contacto_inicial",
  "discovery",
  "showing",
  "cierre",
  "administracion",
] as const;

/** States reachable but shown outside the kanban board (TASK-014). */
export const SPECIAL_STAGES = [
  "venta_cerrada",
  "cerrado_perdido",
  "codigo_azul",
  "seguimiento_futuro",
] as const;

/**
 * Stages that require a completed POSI (Perfil, Objetivos, eStrategias,
 * Issues) before a lead can reach them. Must equal TASK-008's DB trigger
 * list (used by TASK-010 and TASK-015).
 */
export const POSI_GATED_STAGES = [
  "showing",
  "cierre",
  "administracion",
  "venta_cerrada",
] as const;

/** All 10 Almikia stage values, in pipeline order. */
export const ALMIKIA_STAGE_VALUES = [
  INBOX_STAGE,
  ...KANBAN_STAGES,
  ...SPECIAL_STAGES,
] as const;

export type AlmikiaStageValue = (typeof ALMIKIA_STAGE_VALUES)[number];

/** True when `value` is one of the 10 Almikia stage values. */
export const isAlmikiaStageValue = (
  value: string,
): value is AlmikiaStageValue =>
  (ALMIKIA_STAGE_VALUES as readonly string[]).includes(value);

export type DealsByStage = Record<string, Deal[]>;

/**
 * Buckets and orders deals into exactly the given `stages` — never a
 * fallback bucket. A deal whose stage is not one of `stages` (e.g. the
 * prospecto inbox, a special state, or a legacy/unknown value) is dropped
 * from every column. The deal's stored stage is never modified (TASK-014).
 */
export const partitionDealsByStages = (
  unorderedDeals: Deal[],
  stages: readonly string[],
): DealsByStage => {
  const dealsByStage: DealsByStage = stages.reduce(
    (acc, stage) => ({ ...acc, [stage]: [] }),
    {} as DealsByStage,
  );
  unorderedDeals.forEach((deal) => {
    if (dealsByStage[deal.stage]) {
      dealsByStage[deal.stage].push(deal);
    }
  });
  // order each column by index
  stages.forEach((stage) => {
    dealsByStage[stage] = [...dealsByStage[stage]].sort(
      (recordA: Deal, recordB: Deal) => recordA.index - recordB.index,
    );
  });
  return dealsByStage;
};

/** Partitions deals into exactly the 5 kanban columns (TASK-014). */
export const getKanbanDealsByStage = (unorderedDeals: Deal[]): DealsByStage =>
  partitionDealsByStages(unorderedDeals, KANBAN_STAGES);

/**
 * Almikia Epic EP (TASK-104): the minimal shape the derivation helpers below
 * need from an effective stage entry (see
 * `deals/stageConfig.ts#EffectiveDealStage`, a structural superset). Declared
 * locally rather than imported from `stageConfig.ts` -- that module already
 * imports `ALMIKIA_STAGE_VALUES` / `POSI_GATED_STAGES` from here, so importing
 * it back would create a cycle. `placement` is optional so a plain
 * `{ value, label }` fallback entry (`DealStage`, TASK-104) -- which carries
 * no `placement` at all -- is still structurally assignable here; such an
 * entry simply never matches either filter below.
 */
export interface StageListEntry {
  value: string;
  placement?: "inbox" | "kanban" | "special";
}

/**
 * Derives the ordered kanban column key list from the effective stage list
 * (DATA CONTRACT EP (8)), consumed by TASK-106 in place of the E2-only
 * `KANBAN_STAGES` constant. Order is whatever the caller's list is already
 * sorted in (`resolveEffectiveDealStages`'s `(placement, position, key)`
 * order); this only filters by placement.
 */
export function deriveKanbanStageKeys(
  stages: readonly StageListEntry[],
): string[] {
  return stages
    .filter((stage) => stage.placement === "kanban")
    .map((stage) => stage.value);
}

/**
 * Derives the ordered special-state key list from the effective stage list
 * (DATA CONTRACT EP (8)), consumed by TASK-106 in place of the E2-only
 * `SPECIAL_STAGES` constant.
 */
export function deriveSpecialStageKeys(
  stages: readonly StageListEntry[],
): string[] {
  return stages
    .filter((stage) => stage.placement === "special")
    .map((stage) => stage.value);
}

/** Minimal stage-entry shape {@link selectVisibleStages} needs. */
export interface VisibilityStageEntry {
  value: string;
  archived?: boolean;
}

/**
 * Almikia Epic EP (TASK-106): a non-archived stage (kanban column or
 * special-state tab) is always visible, in whatever order the caller's
 * `stages` is already sorted in; an archived one stays visible only while it
 * still holds at least one loaded lead. This is what keeps an archived stage
 * from ever making an existing lead disappear, while an emptied archived
 * stage doesn't clutter the board/tabs forever. Shared by `DealListContent`
 * (kanban columns) and `DealSpecialStates` (tabs) -- never re-implemented
 * per consumer.
 */
export function selectVisibleStages<T extends VisibilityStageEntry>(
  stages: readonly T[],
  dealsByStage: DealsByStage,
): T[] {
  return stages.filter(
    (stage) => !stage.archived || (dealsByStage[stage.value]?.length ?? 0) > 0,
  );
}
